import { find } from "lodash";
import { useCallback, useMemo } from "react";
import { isHotkeyPressed } from "react-hotkeys-hook";
import { useNavigate } from "react-router-dom";

import { resolveStepUrl } from "app/components/build/Show/lib/urlForView";
import { findJobsForStep, useBuild } from "app/components/build/Show/lib/BuildContext";
import { useBuildPreferencesStore } from "app/components/build/Show/lib/useBuildPreferencesStore";
import { InputJob, isInputJobUnblocked } from "app/components/build/Show/lib/types";

import { DisplayMode, getRelevantFilterForStep, useFilterStore } from "../useFilterStore";

export function useStepSearch() {
  const navigate = useNavigate();

  const displayMode = useFilterStore((state) => state.displayMode);
  const expandState = useBuildPreferencesStore((state) => state.expandState);

  const { build } = useBuild();

  const steps = useMemo(
    () =>
      build.steps.filter((step) => {
        if (step.type === "wait") {
          return null;
        }

        if (step.state === "ignored") {
          return false;
        }

        return true;
      }),
    [build.steps],
  );

  const selectStep = useCallback(
    (uuid: string) => {
      const step = find(steps, { uuid });

      if (!step) {
        return;
      }

      // Navigate to the step
      navigate(resolveStepUrl(step.uuid, { open: !isHotkeyPressed("shift") }));

      // Open unblock dialog if necessary
      if (step.type === "input" || step.type === "block") {
        const [job] = findJobsForStep<InputJob>(build, step.uuid);

        if (!isInputJobUnblocked(job)) {
          Turbo.visit(job.unblockDialogPath, { frame: "unblock_dialog" });
        }
      }

      // Ensure the step is visible in the sidebar
      if (displayMode === DisplayMode.State) {
        const state = getRelevantFilterForStep(step, build);

        if (state) {
          expandState(state);
        }
      }

      // Focus the step in the sidebar
      window.requestAnimationFrame(() => {
        const sidebarStep = document.querySelector<HTMLAnchorElement>("[aria-current=step]");

        if (sidebarStep) {
          sidebarStep.focus();
        } else {
          (document.activeElement as HTMLInputElement)?.blur();
        }
      });
    },
    [build, steps, navigate, expandState, displayMode],
  );

  return { steps, selectStep };
}
